<template>
  <div id="AccountScheduledReportsList" class="Main-Content">

    <Page
      :title="$t('Scheduled Reports')"
      :primaryAction="{
        label: $t('scheduledReports.scheduleNewReport'),
        onAction: () => scheduleNewReport(),
        disabled: saving
      }"
      fullWidth
    >
      <Layout>
        <LayoutSection>
          <Card>
            <Filters
              :filters="[
                {
                  key: 'status',
                  label: this.$t('Status'),
                  type: 'select',
                  placeholder: this.$t('Choose a status'),
                  multiple: true,
                  options: [
                    {label: this.$t('Enabled'), value: 'enabled' },
                    {label: this.$t('Disabled'), value: 'disabled'}
                  ]
                }
              ]"
              :query.sync="query"
              :applied-filters="appliedFilters"
              @update:applied-filters="setAppliedFilters"
              @update:query="setQuery"
            />
            <DataTable
              :columns="[
                {
                  key: 'name',
                  label: $t('Name')
                },
                {
                  key: 'period',
                  label: $t('scheduledReports.frequency')
                },
                {
                  key: 'language',
                  label: $t('Language')
                },
                {
                  key: 'status',
                  label: $t('Status')
                },
                {
                  key: 'scheduleStartDate',
                  label: $t('Start Date'),
                  format: 'dateLong'
                }
              ]"
              :rows="formattedReports"
              :row-actions="(row) => getRowActions(row)"
              :loading="loading"
              :pagination="{
                offset: offset,
                limit: limit,
                total: formattedReports.length,
                updateOffset: updateOffset,
                updateLimit: updateLimit
              }"
            >
              <template v-slot:col:name="{row}">
                <PageLink :href="getReportUrl(row.id)">{{row.name}}</PageLink>
              </template>
              <template v-slot:col:language="{row}">
                {{getLanguage(row.language)}}
              </template>
              <template v-slot:col:status="{row}">
                <Badge v-if="row.status === 'enabled'" status="success">{{$t('Enabled')}}</Badge>
                <Badge v-else status="warning">{{$t('Disabled')}}</Badge>
              </template>
            </DataTable>
          </Card>
        </LayoutSection>
      </Layout>

    </Page>

    <Modal
      :title="$t('scheduledReports.runReportForDates')"
      :primaryAction="{
        label: $t('Start'),
        onAction: runForDates,
        disabled: saving || runForDatesError
      }"
      :visible="showRunForDates"
      @close="closeRunForDates"
    >
      <FormLayout v-if="runForDatesObj">
        <DatePicker
          :label="$t('scheduledReports.startDateInclusive')"
          v-model="runForDatesObj.startDate"
        />
        <DatePicker
          :label="$t('scheduledReports.endDateInclusive')"
          v-model="runForDatesObj.endDate"
        />
        <InlineError v-if="runForDatesError">{{runForDatesError}}</InlineError>
        <div v-else>{{$t('scheduledReports.runForXDays', {value: dateRange })}}</div>
      </FormLayout>
    </Modal>

  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'AccountScheduledReportsList',
  data () {
    return {
      loading: true,
      saving: false,
      reports: [],
      query: '',
      appliedFilters: {
        status: null
      },
      runForDatesObj: null,
      showRunForDates: false,
      offset: 0,
      limit: 10
    }
  },
  async mounted() {
    await this.setFilterFromQuery()

    this.$watch('$route', async () => {
      await this.setFilterFromQuery()
    }, { deep: true })

    NProgress.start()
    this.limit = this.getTableLimit('tableLimit') || 10
    this.api.getScheduledReports()
      .then((data) => {
        this.reports = (data || []).map(r => {
          return {
            ...r,
            period: this.periodMap[r.period]
          }
        })   
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.loading = false
        NProgress.done()
      })
  },
  computed: {
    formattedReports() {
      let reports = this.reports.filter(r => {
        if (this.appliedFilters.status && this.appliedFilters.status.length) {
          return this.appliedFilters.status.includes(r.status)
        }
        return true
      }).filter(r => {
        if (this.query) {
          return JSON.stringify(r).toLowerCase().includes(this.query.trim().toLowerCase())
        }
        return true
      })
      return reports
    },
    periodMap() {
      return {
        'DAY': this.$t('Daily'),
        'WEEK': this.$t('Weekly'),
        'MONTH': this.$t('Monthly'),
        'QUARTER': this.$t('Quarterly')
      }
    },
    dateRange() {
      if (this.runForDatesError) return 0
      const justStartDate = window.moment(this.runForDatesObj.startDate).format('YYYY-MM-DD')
      const justEndDate = window.moment(this.runForDatesObj.endDate).format('YYYY-MM-DD')
      return window.moment(justEndDate).diff(window.moment(justStartDate), 'days') + 1
    },
    runForDatesError() {
      if (!this.runForDatesObj || !this.runForDatesObj.startDate || !this.runForDatesObj.endDate) return this.$t('scheduledReports.error.bothDatesSet')
      if (window.moment(this.runForDatesObj.startDate).isAfter(window.moment(this.runForDatesObj.endDate))) return this.$t('scheduledReports.error.startBeforeEnd')
      return false
    }
  },
  methods: {
    getLanguage(lang) {
      const rv = this.supportedLanguages.find(l => l.value === lang)
      return this.$t(`${rv.label}`)
    },
    getReportUrl(reportId) {
      return `/#/account/scheduledReports/edit/${reportId}`
    },
    getRowActions(row) {
      const rv = [
        {
          label: this.$t('Copy'),
          onAction: () => this.copyReport(row.id)
        },
        {
          label: this.$t('Delete'),
          onAction: () => this.deleteReport(row)
        },
        {
          label: this.$t('scheduledReports.viewGenerated'),
          onAction: () => this.viewRuns(row.id)
        }
      ]
      if (row.status === 'enabled') {
        rv.push({
          label: this.$t('scheduledReports.runNow'),
          onAction: () => this.runReport(row)
        })
        rv.push({
          label: this.$t('scheduledReports.runForDates'),
          onAction: () => {
            this.showRunForDates = true
            this.runForDatesObj = {
              id: row.id
            }
          }
        })
        rv.unshift({
          label: this.$t('Disable'),
          onAction: () => this.disableReport(row.id)
        })
      } else if (row.status === 'disabled') {
        rv.unshift({
          label: this.$t('Enable'),
          onAction: () => this.enableReport(row.id)
        })
      }
      return rv
    },
    closeRunForDates() {
      this.showRunForDates = false
      this.runForDatesObj = null
    },
    runForDates() {
      this.saving = true
      this.showRunForDates = false
      this.api.runScheduledReportForDates(this.runForDatesObj)
        .then(async () => {
          await this.$ui.alert({
            title: this.$t('scheduledReports.startedReport'),
            content: this.$t('scheduledReports.startedReportMessage'),
            primaryActionLabel: this.$t('Okay')
          })
          this.runForDatesObj = null
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    async runReport(row) {
      const confirm = await this.$ui.confirm({
        title: this.$t('scheduledReports.runReport'),
        content: this.$t('scheduledReports.runReportMessage', {value: row.name}),
        primaryActionLabel: this.$t('Start'),
        primaryActionType: 'primary'
      })
      if (!confirm) {
        return 
      }
      this.saving = true
      this.api.runScheduledReport(row.id)
        .then(async () => {
          await this.$ui.alert({
            title: this.$t('scheduledReports.startedReport'),
            content: this.$t('scheduledReports.startedReportMessage'),
            primaryActionLabel: this.$t('Okay')
          })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    disableReport(rowId) {
      this.saving = true
      this.api.updateScheduledReportStatus(rowId, 'disabled')
        .then(() => {
          const report = this.reports.find(r => r.id === rowId)
          if (report) report.status = 'disabled'
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    enableReport(rowId) {
      this.saving = true
      this.api.updateScheduledReportStatus(rowId, 'enabled')
        .then(() => {
          const report = this.reports.find(r => r.id === rowId)
          if (report) report.status = 'enabled'
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    async deleteReport(row) {
      const confirm = await this.$ui.confirm({
        title: this.$t('scheduledReports.deleteReport'),
        content: this.$t('scheduledReports.deleteReportMessage', {value: row.name}),
        primaryActionLabel: this.$t('Delete'),
        primaryActionType: 'destructive'
      })
      if (!confirm) {
        return 
      }
      this.saving = true
      this.api.updateScheduledReportStatus(row.id, 'archived')
        .then(() => {
          this.reports = this.reports.filter(r => r.id !== row.id)
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    viewRuns(reportId) {
      this.$router.push({ name: 'AccountScheduledReportsRuns', query: { reportId }})
    },
    copyReport(reportId) {
      this.$router.push({ name: 'AccountScheduledReportsCopy', params: { reportId } })
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    updateOffset(offset) {
      this.offset = offset
    },
    scheduleNewReport() {
      this.$router.push({ name: 'AccountScheduledReportsCreate' })
    },
    resetPagination() {
      this.offset = 0
    },
    setQuery(query) {
      this.query = query
      this.resetPagination()
    },
    setAppliedFilters(filters) {
      this.appliedFilters = filters
      this.resetPagination()
      this.updateUrl()
    },
    updateUrl() {
      this.$router.push({ name: 'AccountScheduledReportsList', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)
    },
    async setFilterFromQuery() {
      if (this.$route.query) {
        this.appliedFilters = {}
        Object.keys(this.$route.query).forEach((k) => {
          if (this.$route.query[k]) {
            if (k === 'status') {
              if (Array.isArray(this.$route.query[k])) {
                this.appliedFilters[k] = this.$route.query[k]
              } else {
                this.appliedFilters[k] = [this.$route.query[k]]
              }
            } else {
              this.appliedFilters[k] = this.$route.query[k]
            }
          } else {
            this.appliedFilters[k] = null
          }
        })
      }
      if (_.isEmpty(this.$route.query)) {
        this.appliedFilters = {}
        this.appliedFilters.status = ['enabled']
      }

      await this.$router.replace({ name: 'AccountScheduledReportsList', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)
    }
  }
}

</script>
